
import React from "react";
import { Achievement } from "@/lib/types";
import { Trophy, Award } from "lucide-react";
import { cn } from "@/lib/utils";
import { 
  EnhancedCard as Card, 
  EnhancedCardContent as CardContent 
} from "@/components/ui/enhanced-card";

export type AchievementLayout = "grid" | "list" | "compact";

interface StaffAchievementsProps {
  achievements: Achievement[];
  layout?: AchievementLayout;
  showIcon?: boolean;
  title?: string;
  className?: string;
  maxItems?: number;
}

const StaffAchievements: React.FC<StaffAchievementsProps> = ({ 
  achievements,
  layout = "grid",
  showIcon = true,
  title = "Achievements",
  className,
  maxItems,
}) => {
  const displayedAchievements = maxItems ? achievements.slice(0, maxItems) : achievements;
  const hasMore = maxItems && achievements.length > maxItems;
  
  const renderAchievementIcon = (achievement: Achievement) => {
    if (!showIcon) return null;
    
    return achievement.icon ? (
      <div className="text-xl">{achievement.icon}</div>
    ) : (
      <Award className="h-5 w-5 text-primary" />
    );
  };
  
  const renderAchievement = (achievement: Achievement, index: number) => {
    // Calculate staggered animation delay based on index
    const animationDelay = `${index * 50}ms`;
    
    return (
      <Card 
        key={achievement.id} 
        className={cn(
          "border-none bg-muted/40",
          layout === "compact" && "p-0",
          "animate-fade-in"
        )} 
        variant="default" 
        animate={true}
        style={{ animationDelay }}
      >
        <CardContent className={cn(
          "p-3 flex items-center gap-2",
          layout === "list" && "border-l-2 border-primary/50"
        )}>
          {renderAchievementIcon(achievement)}
          <div>
            <h5 className="text-xs font-medium">{achievement.name}</h5>
            <p className="text-xs text-muted-foreground">{achievement.description}</p>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  const renderEmptyState = () => (
    <div className="text-center py-4 animate-fade-in">
      <Trophy className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-30" />
      <p className="text-sm text-muted-foreground">
        No achievements yet. Start using your AI assistant to unlock achievements.
      </p>
    </div>
  );
  
  return (
    <div className={cn("space-y-2", className)}>
      <h4 className="text-sm font-medium flex items-center gap-1.5">
        {showIcon && <Trophy className="h-4 w-4 text-primary/80" />}
        {title}
      </h4>
      
      {displayedAchievements.length > 0 ? (
        <div className={cn(
          layout === "grid" && "grid grid-cols-2 gap-2",
          layout === "list" && "space-y-2",
          layout === "compact" && "flex flex-wrap gap-2"
        )}>
          {displayedAchievements.map(renderAchievement)}
        </div>
      ) : (
        renderEmptyState()
      )}
      
      {hasMore && (
        <p className="text-xs text-muted-foreground text-center italic mt-1">
          +{achievements.length - maxItems} more achievements
        </p>
      )}
    </div>
  );
};

export default StaffAchievements;
